import { useQuery, useMutation } from "@tanstack/react-query";
import type { Truck } from "@shared/schema";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function AdminTrucks() {
  const { toast } = useToast();

  const { data: trucks, isLoading, error } = useQuery<Truck[]>({
    queryKey: ["/api/admin/trucks"],
  });

  const deleteTruck = useMutation({
    mutationFn: async (truckId: string) => {
      return apiRequest("DELETE", `/api/admin/trucks/${truckId}`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/trucks"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/analytics"] });
      toast({
        title: "Success",
        description: "Truck deleted successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete truck",
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="text-lg text-muted-foreground">Loading trucks...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="text-lg text-destructive mb-2">Access Denied</div>
          <div className="text-sm text-muted-foreground">You do not have permission to access the admin panel.</div>
        </div>
      </div>
    );
  }

  if (!trucks) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="text-lg text-muted-foreground">No trucks found</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-wide" data-testid="text-admin-trucks-title">Truck Management</h1>
        <p className="text-muted-foreground">Manage and moderate available trucks</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Trucks ({trucks.length})</CardTitle>
          <CardDescription>Complete list of available trucks</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Origin</TableHead>
                <TableHead>Destination</TableHead>
                <TableHead>Capacity (lbs)</TableHead>
                <TableHead>Available From</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {trucks.map((truck) => (
                <TableRow key={truck.id} data-testid={`row-truck-${truck.id}`}>
                  <TableCell className="font-medium">{truck.origin}</TableCell>
                  <TableCell>{truck.destination}</TableCell>
                  <TableCell data-testid={`text-capacity-${truck.id}`}>{truck.capacity.toLocaleString()}</TableCell>
                  <TableCell>
                    {new Date(truck.availableFrom).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Badge 
                      variant={truck.status === "Available" ? "default" : "secondary"}
                      data-testid={`badge-status-${truck.id}`}
                    >
                      {truck.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`button-delete-truck-${truck.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Truck</AlertDialogTitle>
                          <AlertDialogDescription>
                            Are you sure you want to delete this truck? This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => deleteTruck.mutate(truck.id)}
                            data-testid="button-confirm-delete"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
